<template>
  <div class="task-header" @click.stop>
    <div class="header-top">
      <div class="avatar-col">
        <UserIcon
          :user="userList?.find((entry) => entry.username === session.username) || { username: session.username }"
          :size="'36px'"
        />
      </div>

      <div class="top-right-div">
        <div class="top-right-row top-right-row--top">
          <div class="uid-row">
            <div class="uid-id-field">
              <EncodedIdField
                :id="session.id"
                :type="'task'"
                :hideLabel="false"
                :single-line="true"
              />
            </div>
          </div>

          <div v-if="showIcons" class="actions-row">
            <IconButton
              v-if="showLogs"
              v-tooltip="`Logs`"
              class="icon logs-icon"
              :icon="'log'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="emit('logs')"
            />
            <v-btn
              v-if="showDeleteAction"
              icon
              size="x-small"
              variant="text"
              class="more-button"
              aria-label="Task actions"
              @click.stop="emit('delete')"
            >
              <v-icon size="20">mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="top-right-row top-right-row--bottom">
          <div class="task-name" v-tooltip:top="taskName || session.name || 'Unknown'" :title="taskName || session.name || 'Unknown'">{{ taskName || session.name || 'Unknown' }}</div>
        </div>
      </div>
    </div>

    <div class="task-meta-row">
      <span class="task-meta-item task-meta-item--start">{{ startDate || '-' }}</span>
      <div class="task-meta-right">
        <span class="task-meta-item task-meta-item--duration">
          <v-icon size="12" class="task-meta-icon">mdi-timer-outline</v-icon>
          <span>{{ duration || '-' }}</span>
        </span>
        <div class="manage-icon-container">
          <img
            v-if="isAwsBatch"
            v-tooltip="'Batch Task'"
            :src="workflowIcon"
            class="manage-type-svg"
            alt="AWS Batch Task"
          >
          <v-icon
            v-if="isCloud"
            v-tooltip="'Cloud Hosted Task'"
            size="20"
            color="#666"
          >
            mdi-cloud
          </v-icon>
          <v-icon
            v-if="isUserManaged"
            v-tooltip="'User Managed Task'"
            size="20"
            color="#666"
          >
            mdi-laptop
          </v-icon>
          <v-icon
            v-if="isKubernetes"
            v-tooltip="'Kubernetes Managed Task'"
            size="20"
            color="#666"
          >
            mdi-kubernetes
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import IconButton from '@/components/IconButton.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import UserIcon from '@/components/UserIcon.vue';
import workflowIcon from '@/assets/img/svg/workflow.svg';

const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  taskName: {
    type: String,
    default: '',
  },
  startDate: {
    type: String,
    default: '',
  },
  duration: {
    type: String,
    default: '',
  },
  userList: {
    type: Array,
    default: () => [],
  },
  permissions: {
    type: Object,
    default: () => ({}),
  },
  showIcons: {
    type: Boolean,
    default: true,
  },
  showLogs: {
    type: Boolean,
    default: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['logs', 'stop', 'refresh', 'delete']);
const taskName = computed(() => String(props.taskName || '').trim());

const canWrite = computed(() => !!props.permissions?.trainer_write || !!props.permissions?.dataset_write);
const manageType = computed(() => String(props.session?.manage_type || '').trim().toLowerCase());
// User-managed tasks run outside Studio, so Studio must not offer lifecycle controls for them.
const showDeleteAction = computed(() => props.canDelete && canWrite.value && manageType.value !== 'user');
const isAwsBatch = computed(() => manageType.value === 'aws_batch');
const isCloud = computed(() => {
  return manageType.value === '' || manageType.value === 'aws';
});
const isUserManaged = computed(() => {
  return manageType.value === 'user';
});
const isKubernetes = computed(() => {
  return manageType.value === 'kubernetes';
});
</script>

<style scoped lang="scss">
.task-header {
  width: 100%;
  height: fit-content;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0 8px;
}

.header-top {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.avatar-col {
  flex: 0 0 36px;
  width: 36px;
}

.top-right-div {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.top-right-row {
  width: 100%;
  min-width: 0;
}

.top-right-row--top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.top-right-row--bottom {
  display: flex;
  align-items: flex-start;
}

.uid-row {
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.uid-id-field {
  flex: 1 1 auto;
  min-width: 0;
}

.manage-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex: 0 0 20px;
}

.manage-type-svg {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.task-name {
  width: 100%;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e3a8a;
  font-style: italic;
  margin-top: 4px;
  margin-bottom: 4px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-meta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
  width: 100%;
  margin-top: 6px;
  min-width: 0;
}

.task-meta-item {
  font-size: 0.62rem;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-meta-item--start {
  flex: 1 1 auto;
  min-width: 0;
}

.task-meta-item--duration {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
  text-align: right;
}

.task-meta-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}

.task-meta-icon {
  color: #4b5563;
}

.actions-row {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: #4b5563;
}

.actions-row .icon,
.actions-row .more-button {
  align-self: flex-start;
  color: #4b5563;
}

.more-button {
  min-width: 20px;
  width: 20px;
  height: 20px;
  padding: 0;
}

.more-button :deep(.v-btn__content) {
  align-items: flex-start;
}
</style>
